import { useQuery } from "@tanstack/react-query";

import { LinkForward, MessageSquare } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { fetchOdooTicketsLink } from "@/lib/api";
import { useT } from "@/app/localization";

/** Only real chargers (28-digit ids) have tickets in Odoo. */
export const hasOdooTickets = (unitId: string) => /^\d{28}$/.test(unitId);

/**
 * The charger's support tickets and warranty, in VoltiE's Odoo. Admins sign in there with their
 * own accounts; each ticket opens in Helpdesk, and the warranty page shows the sale that
 * delivered the unit. Nothing is shown until the dashboard gives the links (Odoo set up).
 */
export function OdooTicketsLink({ unitId }: { unitId: string }) {
  const t = useT();
  const link = useQuery({
    queryKey: ["odoo-tickets-link", unitId],
    queryFn: () => fetchOdooTicketsLink(unitId),
    staleTime: 10 * 60_000,
    retry: false,
  });

  const url = link.data?.url;
  const warrantyUrl = link.data?.warranty_url;
  if (!url) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="h-4 w-4" /> {t("operator.unit.odoo_title")}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm opacity-70">{t("operator.unit.odoo_description")}</p>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" asChild>
            <a href={url} target="_blank" rel="noopener noreferrer">
              {t("operator.unit.odoo_open")} <LinkForward className="ml-1 h-4 w-4" />
            </a>
          </Button>
          {warrantyUrl && (
            <Button variant="outline" size="sm" asChild>
              <a href={warrantyUrl} target="_blank" rel="noopener noreferrer">
                {t("operator.unit.odoo_warranty")} <LinkForward className="ml-1 h-4 w-4" />
              </a>
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
